/**
 * CreditTierRates — V2 admin page for the typical-mortgage-rate lookup table
 * used by the mortgage readiness projection. Reads/writes
 * `/api/admin/credit-tier-rates`.
 *
 * The 7 tiers ship with sensible defaults; admins can adjust APRs (and
 * min/max/label copy) as the market moves. Model-level validation enforces
 * bounds — this UI is a thin table editor.
 *
 * Wave 6 Batch 3 · Cluster C — full chrome sweep onto `--ds-*` tokens +
 * `admin/_primitives` (TextInput / AccentButton / GhostButton). Every
 * testid + threshold (300-850 score, 0.01-24.99 APR) + API payload
 * preserved byte-exact.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { TextInput, AccentButton, GhostButton } from "@/admin/_primitives";

export default function AdminCreditTierRates() {
  const { has } = useAdminPermissions();
  const canEdit = has("edit_scoring_rubric");
  const [initial, setInitial] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/credit-tier-rates");
      const t = Array.isArray(data?.tiers) ? data.tiers : [];
      setInitial(JSON.parse(JSON.stringify(t)));
      setTiers(JSON.parse(JSON.stringify(t)));
      setUpdatedAt(data?.updated_at || null);
      setUpdatedBy(data?.updated_by || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const dirty = useMemo(() => JSON.stringify(tiers) !== JSON.stringify(initial), [tiers, initial]);

  const patch = (idx, key, v) => {
    setTiers((t) => t.map((row, i) => (i === idx ? { ...row, [key]: v } : row)));
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const body = {
        tiers: tiers.map((t) => ({
          code: String(t.code || "").trim(),
          label: String(t.label || "").trim(),
          min_score: Number(t.min_score),
          max_score: Number(t.max_score),
          typical_apr_pct: Number(t.typical_apr_pct),
        })),
      };
      const { data } = await api.patch("/admin/credit-tier-rates", body);
      const t = Array.isArray(data?.tiers) ? data.tiers : [];
      setInitial(JSON.parse(JSON.stringify(t)));
      setTiers(JSON.parse(JSON.stringify(t)));
      setUpdatedAt(data?.updated_at || null);
      setUpdatedBy(data?.updated_by || null);
      toast.success("Credit tier rates saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const revertAll = () => setTiers(JSON.parse(JSON.stringify(initial)));

  if (loading) {
    return (
      <AdminLayout title="Credit Tier Rates">
        <div className="text-sm text-[var(--ds-color-ink-muted)]">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Credit Tier Rates"
      subtitle="Typical mortgage APR by credit-score tier. Educational estimates only."
      actions={
        canEdit && (
          <div className="flex items-center gap-2">
            <GhostButton
              data-testid="credit-tier-rates-revert"
              onClick={revertAll}
              disabled={!dirty || saving}
            >
              <RotateCcw className="w-3 h-3" /> Revert
            </GhostButton>
            <AccentButton
              data-testid="credit-tier-rates-save"
              onClick={save}
              disabled={!dirty || saving}
            >
              {saving ? "Saving…" : "Save changes"}
            </AccentButton>
          </div>
        )
      }
    >
      <AdminPageHelp
        title="Credit Tier Rates"
        purpose="Populates the credit-tier → typical-APR table used by mortgage payment estimates and purchase-power calculations across the app."
        configures="One row per credit-score band. Edit the APR, score range, and human label for each tier."
        impact="Every mortgage-readiness view and every purchase-power estimate refreshes on next page load. Users see the new APR in their monthly payment calculation."
        safety="Yes. Use Revert to drop unsaved edits, or paste the previous APR back and Save. Model-level validation refuses invalid APRs (>= 25%) or out-of-range scores."
      />

      {!canEdit && (
        <div
          className="mb-4 rounded-[var(--ds-radius-card)] border px-3 py-2 text-xs"
          style={{
            borderColor: "var(--ds-color-warning)",
            background: "var(--ds-color-warning-soft)",
            color: "var(--ds-color-warning)",
          }}
        >
          Your role can view these rates but cannot save changes.
        </div>
      )}

      <div
        className="rounded-[var(--ds-radius-card)] border overflow-hidden"
        style={{
          borderColor: "var(--ds-color-admin-border)",
          background: "var(--ds-color-surface)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr
                style={{
                  background: "var(--ds-color-surface-muted)",
                  color: "var(--ds-color-ink-muted)",
                }}
              >
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-[0.16em] font-semibold whitespace-nowrap">Code</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-[0.16em] font-semibold w-28 whitespace-nowrap">Label</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-[0.16em] font-semibold w-24 whitespace-nowrap">Min score</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-[0.16em] font-semibold w-24 whitespace-nowrap">Max score</th>
                <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-[0.16em] font-semibold w-28 whitespace-nowrap">Typical APR (%)</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((row, idx) => (
                <tr
                  key={row.code || idx}
                  data-testid={`credit-tier-row-${row.code || idx}`}
                  className="border-t"
                  style={{ borderColor: "var(--ds-color-admin-border)" }}
                >
                  <td
                    className="px-3 py-2 text-xs font-mono tabular-nums"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                  >
                    {row.code}
                  </td>
                  <td className="px-3 py-2">
                    <TextInput
                      type="text"
                      value={row.label || ""}
                      disabled={!canEdit}
                      onChange={(e) => patch(idx, "label", e.target.value)}
                      data-testid={`credit-tier-label-${row.code || idx}`}
                      className="w-full"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <TextInput
                      type="number"
                      value={row.min_score ?? ""}
                      min={300}
                      max={850}
                      disabled={!canEdit}
                      onChange={(e) => patch(idx, "min_score", e.target.value === "" ? "" : Number(e.target.value))}
                      data-testid={`credit-tier-min-${row.code || idx}`}
                      className="w-full tabular-nums"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <TextInput
                      type="number"
                      value={row.max_score ?? ""}
                      min={300}
                      max={850}
                      disabled={!canEdit}
                      onChange={(e) => patch(idx, "max_score", e.target.value === "" ? "" : Number(e.target.value))}
                      data-testid={`credit-tier-max-${row.code || idx}`}
                      className="w-full tabular-nums"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <TextInput
                      type="number"
                      value={row.typical_apr_pct ?? ""}
                      step={0.05}
                      min={0.01}
                      max={24.99}
                      disabled={!canEdit}
                      onChange={(e) => patch(idx, "typical_apr_pct", e.target.value === "" ? "" : Number(e.target.value))}
                      data-testid={`credit-tier-apr-${row.code || idx}`}
                      className="w-full tabular-nums"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>
        Last updated: {updatedAt ? new Date(updatedAt).toLocaleString() : "—"}
        {updatedBy ? ` · by ${updatedBy}` : ""}
      </div>
    </AdminLayout>
  );
}
